import { useCallback, useState } from "react"

import { scannedCodeSchema } from "../schemas/scannedCodeSchema"
import { type ScannedCode } from "../types"

import { useGetPalletByReferenceId } from "./useGetPalletByReferenceId"
import { useScannerDetection } from "./useScannerDetection"

import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { usePutAwayPallets } from "@/screens/WarehouseReceiving/hooks/usePutAwayPallets"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface PalletUIState {
  repeatedScan: boolean
  scanCount: number
}

interface UsePalletScanningReturn {
  scannedPallets: GoodsReceiptPallet[]
  palletUIState: Map<number, PalletUIState>
  storedPallets: GoodsReceiptPallet[]
  handleCloseModal: () => void
  handleFinishStoring: () => void
  updatePallet: (pallet: GoodsReceiptPallet) => void
  clearStoredSuccess: () => void
}

export const usePalletScanning = (): UsePalletScanningReturn => {
  const [scannedPallets, setScannedPallets] = useState<GoodsReceiptPallet[]>([])
  const [palletUIState, setPalletUIState] = useState<Map<number, PalletUIState>>(new Map())
  const [storedPallets, setStoredPallets] = useState<GoodsReceiptPallet[]>([])
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { fetchPallet } = useGetPalletByReferenceId()
  const { putAwayPallets } = usePutAwayPallets()

  const handleScan = async (result: { error: boolean; success: { scannedCode: ScannedCode } | null }) => {
    if (result.error) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: "Invalid or unrecognized pallet QR code.",
      })
      return
    }

    if (result.success) {
      const validation = scannedCodeSchema.safeParse(result.success.scannedCode)

      if (!validation.success) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: "Invalid or unrecognized pallet QR code.",
        })
        return
      }

      const existingPallet = scannedPallets.find((p) => String(p.referenceId) === String(validation.data.refId))

      if (existingPallet) {
        setPalletUIState((prevState) => {
          const newState = new Map(prevState)
          const currentState = newState.get(existingPallet.referenceId) || { repeatedScan: false, scanCount: 1 }
          newState.set(existingPallet.referenceId, {
            repeatedScan: true,
            scanCount: currentState.scanCount + 1,
          })
          return newState
        })
        return
      }

      const scannedPallet = await fetchPallet(Number(validation.data.refId))

      if (!scannedPallet) {
        return
      }

      setScannedPallets((prev) => [...prev, scannedPallet])
      setPalletUIState((prevState) => {
        const newState = new Map(prevState)
        newState.set(scannedPallet.referenceId, { repeatedScan: false, scanCount: 1 })
        return newState
      })
    }
  }

  useScannerDetection({
    onScan: (result) => {
      void handleScan(result)
    },
  })

  const handleCloseModal = () => {
    setScannedPallets([])
    setPalletUIState(new Map())
  }

  const handleFinishStoring = async () => {
    if (scannedPallets.length === 0) {
      return
    }

    const palletIds = scannedPallets.map((pallet) => pallet.goodsReceiptPalletId)
    const result = await putAwayPallets(palletIds)

    if (result.success) {
      setStoredPallets(result.data || [])

      setScannedPallets([])
      setPalletUIState(new Map())
    } else {
      notify({
        type: NotificationType.Error,
        message: result.error || "Failed to store pallets. Please try again.",
      })
    }
  }

  const updatePallet = useCallback((updatedPallet: GoodsReceiptPallet) => {
    setScannedPallets((prev) => prev.map((p) => (p.referenceId === updatedPallet.referenceId ? updatedPallet : p)))
  }, [])

  const clearStoredSuccess = useCallback(() => {
    setStoredPallets([])
  }, [])

  return {
    scannedPallets,
    palletUIState,
    storedPallets,
    handleCloseModal,
    handleFinishStoring,
    updatePallet,
    clearStoredSuccess,
  }
}
